// src/components/ChargementAtomes.jsx — LE CHARGEMENT EN ATOMES (variante
// « Condensation », prévue pour ERPIA V0.2, demandée sur l'optimiseur le
// 31.10). Le nuage de points de l'agent se condense en mot pendant que le
// calcul tourne : les atomes viennent prendre leur place un à un, la marque
// apparaît. La fin n'est pas connue : le mot se forme jusqu'à 90 % environ
// puis respire, et le composant disparaît quand l'écran a sa réponse.
//
// Sobre : rien avant 300 ms (une lecture rapide ne clignote pas) ; mouvement
// réduit → le mot pointillé, immobile ; une toile, aucune bibliothèque ; les
// couleurs viennent des jetons de l'écran (CSS), jamais du code.
import React, { useEffect, useRef, useState } from 'react';

const DELAI_MS = 300;

export default function ChargementAtomes({ libelle = '', mot = 'ERPIA', hauteur = 132 }) {
  const toileRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setVisible(true), DELAI_MS);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const toile = toileRef.current;
    if (!toile) return undefined;
    const ctx = toile.getContext('2d');
    if (!ctx) return undefined;
    const reduit = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    let L = 0, H = 0, atomes = [], rafId = 0, t0 = performance.now(), phase = 'nuage';
    // Tirage déterministe : la même forme à chaque ouverture.
    let graine = 7;
    const alea = () => { graine = (graine * 9301 + 49297) % 233280; return graine / 233280; };

    const cibles = (texte) => {
      const off = document.createElement('canvas');
      off.width = L; off.height = H;
      const o = off.getContext('2d');
      let taille = Math.min(H * 0.62, 120);
      const police = (px) => `800 ${px}px "DM Sans", "Helvetica Neue", Arial, sans-serif`;
      o.font = police(taille);
      const largeur = o.measureText(texte).width || 1;
      taille = Math.min(taille, taille * (L * 0.8) / largeur);
      o.font = police(taille);
      if ('letterSpacing' in o) o.letterSpacing = `${-0.02 * taille}px`;
      o.textAlign = 'center'; o.textBaseline = 'middle'; o.fillStyle = 'black';
      o.fillText(texte, L / 2, H * 0.5);
      const img = o.getImageData(0, 0, L, H).data;
      const pts = [];
      const pas = Math.max(2, Math.round(Math.sqrt((L * H) / 9000)));
      for (let y = 0; y < H; y += pas) for (let x = 0; x < L; x += pas) {
        if (img[(y * L + x) * 4 + 3] > 140) pts.push([x + (alea() - 0.5) * pas * 0.9, y + (alea() - 0.5) * pas * 0.9]);
      }
      for (let i = pts.length - 1; i > 0; i--) { const j = Math.floor(alea() * (i + 1)); [pts[i], pts[j]] = [pts[j], pts[i]]; }
      return pts;
    };

    const construire = () => {
      graine = 7;
      const pts = cibles(String(mot || 'ERPIA').toUpperCase().slice(0, 10));
      const n = pts.length;
      const angle = Math.PI * (3 - Math.sqrt(5));
      atomes = pts.map(([tx, ty], i) => {
        const y = 1 - (i / Math.max(1, n - 1)) * 2, r = Math.sqrt(1 - y * y), th = angle * i, k = 0.55 + alea() * 0.5;
        return { tx, ty, x: L / 2, y: H / 2, vx: 0, vy: 0, sx: Math.cos(th) * r * k, sy: y * k, sz: Math.sin(th) * r * k,
          rang: alea() * 0.9, phase: alea() * Math.PI * 2, taille: 0.7 + alea() * 1.0, corail: alea() < 0.03, pose: false };
      });
      if (reduit) atomes.forEach(p => { p.x = p.tx; p.y = p.ty; p.pose = true; });
    };

    const dimensionner = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const r = toile.getBoundingClientRect();
      L = Math.max(200, Math.round(r.width)); H = Math.max(80, Math.round(r.height));
      toile.width = L * dpr; toile.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      construire();
    };

    // La part du mot formée : rien pendant le nuage, vite jusqu'à 60 %, puis
    // de plus en plus lentement vers 90 % — on ne connaît pas la fin.
    const progres = (t) => {
      const s = t / 1000 - 1.2;
      if (s <= 0) return 0;
      return s < 1.6 ? (s / 1.6) * 0.6 : 0.6 + 0.3 * (1 - Math.exp(-(s - 1.6) / 3));
    };

    const dessiner = (t) => {
      const style = getComputedStyle(toile);
      const encre = style.color;
      const corail = style.getPropertyValue('--atome-corail').trim() || encre;
      ctx.clearRect(0, 0, L, H);
      for (const coul of [false, true]) {
        ctx.fillStyle = coul ? corail : encre;
        for (const p of atomes) {
          if (p.corail !== coul) continue;
          let alpha = p.pose ? 0.92 : 0.55, s = p.taille;
          if (phase === 'nuage' && p.z !== undefined) { alpha = 0.3 + (p.z + 1) * 0.3; s *= 0.7 + (p.z + 1) * 0.35; }
          if (coul) alpha = Math.min(1, alpha * (0.75 + 0.25 * Math.sin(t * 0.006 + p.phase)));
          ctx.globalAlpha = alpha;
          ctx.fillRect(p.x - s / 2, p.y - s / 2, s, s);
        }
      }
      ctx.globalAlpha = 1;
    };

    const pas = (now) => {
      const t = now - t0;
      if (phase === 'nuage' && t > 1200) phase = 'condensation';
      const part = progres(t);
      const cx = L / 2, cy = H / 2, R = Math.min(L, H) * 0.34;
      const rot = t * 0.00035, cr = Math.cos(rot), sr = Math.sin(rot), souffle = 1 + Math.sin(t * 0.0016) * 0.04;
      for (const p of atomes) {
        if (phase === 'nuage') {
          // Le nuage de l'agent : une sphère qui tourne et respire.
          const x = p.sx * cr - p.sz * sr, z = p.sx * sr + p.sz * cr;
          p.x = cx + x * R * souffle; p.y = cy + p.sy * R * souffle; p.z = z;
          continue;
        }
        p.z = undefined;
        if (part >= p.rang) {
          p.vx += (p.tx - p.x) * 0.045; p.vy += (p.ty - p.y) * 0.045; p.vx *= 0.82; p.vy *= 0.82;
          p.x += p.vx; p.y += p.vy;
          if (!p.pose && Math.hypot(p.tx - p.x, p.ty - p.y) < 0.8) p.pose = true;
          if (p.pose) { p.x = p.tx + Math.sin(t * 0.003 + p.phase) * 0.45; p.y = p.ty + Math.cos(t * 0.0027 + p.phase) * 0.45; }
        } else {
          // Pas encore son tour : mouvement brownien, retenu près du centre.
          p.vx += (alea() - 0.5) * 0.35 + (cx - p.x) * 0.0008; p.vy += (alea() - 0.5) * 0.35 + (cy - p.y) * 0.0012;
          p.vx *= 0.96; p.vy *= 0.96; p.x += p.vx; p.y += p.vy;
        }
      }
      dessiner(t);
      rafId = requestAnimationFrame(pas);
    };

    dimensionner();
    if (reduit) { phase = 'condensation'; dessiner(0); }
    else rafId = requestAnimationFrame(pas);
    const surRedim = () => { dimensionner(); if (reduit) dessiner(0); };
    window.addEventListener('resize', surRedim);
    return () => { cancelAnimationFrame(rafId); window.removeEventListener('resize', surRedim); };
  }, [visible, mot]);

  return (
    <div className="chargement-atomes" role="status" aria-live="polite">
      {visible && <canvas ref={toileRef} className="chargement-atomes__toile" style={{ height: `${hauteur}px` }} aria-hidden="true" />}
      {libelle && <p className="chargement-atomes__libelle">{libelle}</p>}
    </div>
  );
}
